import { find, get } from 'lodash';
import { BookingErrorCodes } from '../actions/constants';
import {
    APPOINTMENT_TIME,
    FIELD_ERROR_CODES,
} from '../components/AppointmentRequirements/constants';
import {
    hideModal,
    showModal,
} from '../components/BookingContainer/BookingContainerActions';
import {
    appointmentTimeErrorModalContent,
    didYouStepAwayModalContent,
    genericErrorModalContent,
    customErrorModalContent,
    syncFailureModalContent,
    maxAppointmentsReachedModalContent,
    maxVaccineAppointmentsReachedModalContent,
    patientTimesOverlapErrorModalContent,
    phoneIsVerifiedFailureErrorModalContent,
} from '../components/ModalContent';
import {
    BOOKING_STATUS,
    BookingStateProperties,
    HttpStatusCodes,
} from '../constants';
import { paths } from '../paths';
import { pushWithQuery } from './pushWithQuery';

export const redirectOnErrorResponse = (
    dispatch,
    {
        bookingId,
        bookingErrorCode,
        bookingErrors,
        bookingState,
        responseStatus,
        providerLocationPhone,
    }
) => {
    const clearErrorsHideModal = () => {
        dispatch(hideModal());
    };

    const bookingIdToUse =
        bookingId || get(bookingState, [BookingStateProperties.bookingId]);

    const procedureId = get(bookingState, [BookingStateProperties.procedureId]);

    if (responseStatus === HttpStatusCodes.UnprocessableEntity) {
        if (
            get(bookingState, [BookingStateProperties.status]) ===
            BOOKING_STATUS.COMPLETE
        ) {
            dispatch(
                pushWithQuery(`${paths.bookingCompletePath}/${bookingIdToUse}`)
            );
        } else if (
            get(bookingState, [BookingStateProperties.status]) ===
            BOOKING_STATUS.FAILED
        ) {
            const redirectToProfile = () => {
                const href = getProfileLinkFromBookingState(bookingState);
                window.location.assign(href);
            };
            dispatch(showModal(genericErrorModalContent(redirectToProfile)));
        } else {
            dispatch(showModal(genericErrorModalContent(clearErrorsHideModal)));
        }

        return;
    } else if (
        responseStatus === HttpStatusCodes.Unauthorized ||
        responseStatus === HttpStatusCodes.Forbidden
    ) {
        dispatch(pushWithQuery(`${paths.startSignInPath}/${bookingIdToUse}`));
        dispatch(showModal(didYouStepAwayModalContent(clearErrorsHideModal)));
        return;
    } else if (responseStatus === HttpStatusCodes.NotFound) {
        window.location.assign(paths.medicalTeamPath);
        return;
    }

    if (
        get(bookingState, [BookingStateProperties.status]) ===
        BOOKING_STATUS.FAILED
    ) {
        const redirectToHomepage = () => {
            window.location.assign('/');
        };
        dispatch(
            showModal(
                syncFailureModalContent(
                    redirectToHomepage,
                    providerLocationPhone
                )
            )
        );
        return;
    }

    const path = paths.reviewAndBookPath;
    const nextPath = `${path}/${bookingIdToUse}`;

    // Process Booking Errors
    handleBookingErrors({
        dispatch,
        bookingErrorCode,
        bookingErrors,
        nextPath,
        clearErrorsHideModal,
        procedureId,
    });
};

const handleBookingErrors = ({
    dispatch,
    bookingErrorCode,
    bookingErrors = [],
    nextPath,
    clearErrorsHideModal,
    procedureId,
}) => {
    dispatch(pushWithQuery(nextPath));

    const patientTimesOverlapErrorExists = find(
        bookingErrors,
        (error) => error.code === FIELD_ERROR_CODES.PATIENT_TIMES_OVERLAP
    );

    const phoneIsVerifiedFailureErrorExists = find(
        bookingErrors,
        (error) => error.code === FIELD_ERROR_CODES.PHONE_IS_VERIFIED_FAILURE
    );

    const appointmentTimeErrorExists = find(
        bookingErrors,
        (error) => error.name === APPOINTMENT_TIME
    );

    // covid errors do not dispatch a modal -- force full page to show covid error
    const isCovidError = hasCovidError(bookingErrors);

    const errorWithCustomMessageExists =
        bookingErrors.length && bookingErrors[0].message;

    if (isCovidError) {
        return;
    }
    if (bookingErrorCode === BookingErrorCodes.MaxAppointmentsReached) {
        dispatch(
            showModal(
                maxAppointmentsReachedModalContent(
                    redirectToUpcomingAppointments,
                    clearErrorsHideModal
                )
            )
        );
    } else if (
        bookingErrorCode === BookingErrorCodes.MaxVaccineAppointmentsReached
    ) {
        dispatch(
            showModal(
                maxVaccineAppointmentsReachedModalContent(
                    redirectToUpcomingAppointments,
                    clearErrorsHideModal,
                    procedureId
                )
            )
        );
    } else if (bookingErrorCode === BookingErrorCodes.TimeslotGoneException) {
        dispatch(
            showModal(appointmentTimeErrorModalContent(clearErrorsHideModal))
        );
    } else if (patientTimesOverlapErrorExists) {
        dispatch(
            showModal(
                patientTimesOverlapErrorModalContent(clearErrorsHideModal)
            )
        );
    } else if (phoneIsVerifiedFailureErrorExists) {
        dispatch(
            showModal(
                phoneIsVerifiedFailureErrorModalContent(clearErrorsHideModal)
            )
        );
    } else if (appointmentTimeErrorExists) {
        dispatch(
            showModal(appointmentTimeErrorModalContent(clearErrorsHideModal))
        );
    } else if (errorWithCustomMessageExists) {
        dispatch(
            showModal(
                customErrorModalContent(
                    clearErrorsHideModal,
                    bookingErrors[0].message
                )
            )
        );
    } else {
        dispatch(showModal(genericErrorModalContent(clearErrorsHideModal)));
    }
};

const redirectToUpcomingAppointments = () => {
    window.location.assign('/patient/upcomingappointments');
};

export const getProfileLinkFromBookingState = (bookingState) => {
    if (!bookingState) {
        return paths.medicalTeamPath;
    }

    return (
        '/doctor/' +
        `${bookingState[BookingStateProperties.providerId]}?` +
        `LocIdent=${bookingState[BookingStateProperties.locationId]}&` +
        `reason_visit=${bookingState[BookingStateProperties.procedureId]}&` +
        `insuranceCarrier=${
            bookingState[BookingStateProperties.insuranceCarrierId]
        }&` +
        `insurancePlan=${bookingState[BookingStateProperties.insurancePlanId]}`
    );
};

function hasCovidError(bookingErrors) {
    return !!find(
        bookingErrors,
        (f = {}) => f.name === 'requirements.corona_screen_symptoms'
    );
}
